import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import Flex from "@/components/Flex";
import { NAVBAR_HEIGHT } from "@/components/app/nav-bar";
import Text from "@/components/Text";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { PROVIDER_PATHS } from "@/constants/paths";
import useDisclosure from "@/hooks/use-disclosure";
import { useScrollSpy } from "@/hooks/use-scroll-spy";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  GROUP_VISIT_DETAIL_QUERY_KEY,
  useGroupVisitById,
} from "@/queries/group-visit/use-group-visit-by-id";
import { GROUP_VISIT_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-visit-list";
import { useUpdateGroupVisit } from "@/queries/group-visit/use-update-group-visit";
import { useUpdateGroupVisitArchive } from "@/queries/group-visit/use-update-group-visit-archive";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import ChipSelector from "@/components/ChipSelector";
import GroupForm, { getGroupSectionNav } from "./components/GroupForm";
import {
  GROUP_ACTION_LABEL,
  GROUP_CONFIRMATION,
  GROUP_DETAIL_LABEL,
  GROUP_EDIT_SECTIONS,
  GROUP_FORM_MAX_WIDTH,
  GROUP_FORM_MODE,
  GROUP_LIST_LABEL,
  GROUP_MESSAGE,
  GROUP_TITLE,
  GROUP_VIEW_TITLE,
} from "./group-visit-constant";
import {
  canArchiveGroup,
  getGroupDetailPath,
  toGroupDetailFromGroupVisit,
  toGroupVisitPayload,
} from "./group-visit-utils";
import { useGroupForm } from "./hooks/use-group-form";

/**
 * Full-height shell for the edit screen. The page itself must not scroll —
 * only the form region does — or the header actions scroll out of reach.
 */
const GroupEditShell = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      height: `calc(100svh - ${NAVBAR_HEIGHT})`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: CHART_COLORS.surfaceDefault,
    }}
  >
    {children}
  </Box>
);

export type GroupEditLayoutProps = {
  /** Opens the group from the View action with every field disabled. */
  readOnly?: boolean;
};

/**
 * Routed Edit Group Details screen. Both entry points — the list action menu
 * and the Group Details button — land here, so the saved values are identical.
 *
 * @author Rajkumar Rathod
 */
const GroupEditLayout = ({ readOnly = false }: GroupEditLayoutProps) => {
  const { groupVisitId } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const discardDisclosure = useDisclosure();
  const archiveDisclosure = useDisclosure();

  const scrollSpy = useScrollSpy({
    sectionIds: GROUP_EDIT_SECTIONS,
    containerRef: scrollContainerRef,
  });

  const {
    data: savedGroup,
    isLoading,
    isError,
  } = useGroupVisitById(groupVisitId);

  const { mutateAsync: updateGroupVisit } = useUpdateGroupVisit();
  const { mutateAsync: updateGroupVisitArchive } = useUpdateGroupVisitArchive();

  const group = useMemo(
    () => (savedGroup ? toGroupDetailFromGroupVisit(savedGroup) : undefined),
    [savedGroup]
  );

  const goBack = () => navigate(PROVIDER_PATHS.GROUP_VISIT);

  const refreshGroup = () => {
    queryClient.invalidateQueries({ queryKey: [GROUP_VISIT_LIST_QUERY_KEY] });
    queryClient.invalidateQueries({
      queryKey: [GROUP_VISIT_DETAIL_QUERY_KEY, groupVisitId],
    });
  };

  const { formik, isArchived } = useGroupForm({
    mode: GROUP_FORM_MODE.EDIT,
    sections: GROUP_EDIT_SECTIONS,
    group,
    onSubmit: async () => {
      if (!savedGroup) return;

      try {
        const res = await updateGroupVisit({
          groupVisit: toGroupVisitPayload(formik.values, savedGroup),
        });

        refreshGroup();
        showSnackbar(getSuccessSnackbar(res));
        if (groupVisitId) navigate(getGroupDetailPath(groupVisitId));
        else goBack();
      } catch (error) {
        showSnackbar(getErrorSnackbar(error));
      }
    },
  });

  const isReadOnly = readOnly || isArchived;

  const handleCancel = () => {
    if (formik.dirty && !isReadOnly) {
      discardDisclosure.open();
      return;
    }
    goBack();
  };

  const handleArchive = async () => {
    if (!groupVisitId) return;

    try {
      const res = await updateGroupVisitArchive({
        groupVisitId,
        status: true,
      });

      refreshGroup();
      showSnackbar(getSuccessSnackbar(res));
      archiveDisclosure.close();
      goBack();
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    }
  };

  if (isLoading) {
    return (
      <GroupEditShell>
        <Box sx={{ p: 2 }}>
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textLabel}>
            {GROUP_MESSAGE.loadingGroup}
          </Text>
        </Box>
      </GroupEditShell>
    );
  }

  if (isError || !savedGroup) {
    return (
      <GroupEditShell>
        <Flex flexDirection="column" gap={1} alignItems="flex-start" p={2}>
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textLabel}>
            {GROUP_MESSAGE.loadGroupError}
          </Text>
          <StyledButtonV2
            label={GROUP_DETAIL_LABEL.backToGroups}
            variant="outlined"
            onClick={goBack}
          />
        </Flex>
      </GroupEditShell>
    );
  }

  const canArchive = canArchiveGroup(group?.status);

  return (
    <GroupEditShell>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
        sx={{
          flexShrink: 0,
          backgroundColor: CHART_COLORS.surfaceWhite,
          borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
          px: 2,
          py: 1.5,
        }}
      >
        <Flex alignItems="center" gap={1}>
          <ArrowBackIcon
            role="button"
            tabIndex={0}
            aria-label={GROUP_DETAIL_LABEL.backToGroups}
            onClick={handleCancel}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") handleCancel();
            }}
            sx={{
              fontSize: 16,
              cursor: "pointer",
              color: CHART_COLORS.btnSecondaryText,
            }}
          />
          <Text
            component="h1"
            {...CHART_TYPOGRAPHY.body3}
            fontWeight={CHART_TYPOGRAPHY.weight.medium}
            c={CHART_COLORS.textDarker}
          >
            {`${readOnly ? GROUP_VIEW_TITLE : GROUP_TITLE[GROUP_FORM_MODE.EDIT]} - ${group?.groupName ?? ""}`}
          </Text>
        </Flex>

        <Flex alignItems="center" gap={1}>
          {!isReadOnly && (
            <StyledButtonV2
              label={GROUP_ACTION_LABEL.archive}
              variant="text"
              disabled={!canArchive}
              onClick={archiveDisclosure.open}
            />
          )}

          <StyledButtonV2
            label={
              readOnly ? GROUP_ACTION_LABEL.close : GROUP_ACTION_LABEL.cancel
            }
            variant="outlined"
            onClick={handleCancel}
          />

          {!isReadOnly && (
            <StyledButtonV2
              label={GROUP_ACTION_LABEL.save}
              variant="primary"
              disabled={formik.isSubmitting || !formik.dirty}
              onClick={() => formik.handleSubmit()}
            />
          )}
        </Flex>
      </Flex>

      <Box sx={{ flexShrink: 0, px: 2, pt: 2 }}>
        <Box sx={{ maxWidth: GROUP_FORM_MAX_WIDTH, mx: "auto" }}>
          <ChipSelector
            items={getGroupSectionNav(GROUP_EDIT_SECTIONS)}
            activeId={scrollSpy.activeId}
            onSelect={scrollSpy.scrollTo}
            ariaLabel={GROUP_LIST_LABEL.sectionNavLabel}
          />
        </Box>
      </Box>

      {/* minHeight lets this flex child shrink so the overflow is its own. */}
      <Box
        ref={scrollContainerRef}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2 }}
      >
        <Box
          sx={{
            maxWidth: GROUP_FORM_MAX_WIDTH,
            mx: "auto",
            backgroundColor: CHART_COLORS.surfaceWhite,
            borderRadius: CHART_DIMENSIONS.borderRadius,
            p: 3,
            pt: 4,
          }}
        >
          <GroupForm
            formik={formik}
            sections={GROUP_EDIT_SECTIONS}
            disabled={isReadOnly}
            scrollContainerRef={scrollContainerRef}
            scrollSpy={scrollSpy}
          />
        </Box>
      </Box>

      <ConfirmationModal
        disclosureHost={discardDisclosure}
        title={GROUP_CONFIRMATION.discardTitle}
        content={GROUP_CONFIRMATION.discardContent}
        confirmationText={GROUP_CONFIRMATION.discardConfirm}
        onConfirm={goBack}
      />

      <ConfirmationModal
        disclosureHost={archiveDisclosure}
        title={GROUP_CONFIRMATION.archiveTitle}
        content={GROUP_CONFIRMATION.archiveContent}
        confirmationText={GROUP_CONFIRMATION.archiveConfirm}
        onConfirm={handleArchive}
      />
    </GroupEditShell>
  );
};

export default GroupEditLayout;
